import type { ReactNode } from 'react'
import { HugeiconsIcon } from '@hugeicons/react'
import { Cancel01Icon } from '@hugeicons/core-free-icons'
import { cn } from '@/lib/utils'

/**
 * Full-bleed status strip under the shell header (DESIGN.md "Urgency Scale").
 * Amber = soft nudge (ink button, no price, dismissible), rust = last 24h
 * (rust button with the price, not dismissible), ink = blocking or read-only
 * (white button with the price). Presentational: the caller picks tone, copy
 * and what the CTA does.
 */
export type UrgencyTone = 'amber' | 'rust' | 'ink'

const TONE: Record<UrgencyTone, { bar: string; text: string; chip: string; chipText: string; button: string }> = {
  amber: {
    bar: 'bg-urgency-amber-bg border-b border-urgency-amber-accent/30',
    text: 'text-urgency-amber-fg',
    chip: 'bg-urgency-amber-accent/20',
    chipText: 'text-urgency-amber-fg',
    button: 'h-7 bg-urgency-ink-bg text-white text-xs font-semibold hover:bg-urgency-ink-bg/90',
  },
  rust: {
    bar: 'bg-urgency-rust-bg border-b border-urgency-rust-accent/30',
    text: 'text-urgency-rust-fg',
    chip: 'bg-urgency-rust-accent',
    chipText: 'text-white',
    button: 'h-7 bg-urgency-rust-accent text-white text-xs font-semibold hover:bg-urgency-rust-accent/90',
  },
  ink: {
    bar: 'bg-urgency-ink-bg',
    text: 'text-urgency-ink-fg',
    chip: 'bg-urgency-rust-accent',
    chipText: 'text-white',
    button: 'h-7 bg-white text-urgency-ink-bg text-xs font-semibold hover:bg-white/90',
  },
}

export interface UrgencyBannerProps {
  tone: UrgencyTone
  /** Short status chip at the left ("3 days left", "Payment failed"). */
  chip: string
  /** Glyph or mini meter inside the chip. */
  chipIcon?: ReactNode
  /** Override the chip fill (the read-only strip uses a translucent white chip). */
  chipClassName?: string
  message: ReactNode
  cta?: { label: string; onClick: () => void; disabled?: boolean }
  /** Larger CTA (32px / 13px), used by the read-only strip. */
  ctaSize?: 'sm' | 'md'
  /** Quiet note before the CTA, e.g. "Opens Stripe billing". */
  hint?: string
  onDismiss?: () => void
  className?: string
}

export function UrgencyBanner({
  tone,
  chip,
  chipIcon,
  chipClassName,
  message,
  cta,
  ctaSize = 'sm',
  hint,
  onDismiss,
  className,
}: UrgencyBannerProps) {
  const t = TONE[tone]
  const blocking = tone !== 'amber'
  return (
    <div
      role={blocking ? 'alert' : 'status'}
      className={cn(
        'flex shrink-0 flex-wrap items-center gap-x-3 gap-y-2 py-2.5 pl-5 pr-4 sm:min-h-12 sm:flex-nowrap sm:py-2',
        t.bar,
        className,
      )}
    >
      <span
        className={cn(
          'inline-flex h-[22px] shrink-0 items-center gap-1.5 rounded-full px-2 text-xs font-semibold leading-4',
          t.chip,
          t.chipText,
          chipClassName,
        )}
      >
        {chipIcon}
        {chip}
      </span>
      <p className={cn('min-w-0 basis-full text-[13px] font-medium leading-4 sm:basis-auto sm:flex-1', t.text)}>
        {message}
      </p>
      {hint && <span className={cn('hidden shrink-0 text-xs leading-4 md:inline', t.text)}>{hint}</span>}
      {cta && (
        <button
          type="button"
          onClick={cta.onClick}
          disabled={cta.disabled}
          className={cn(
            'inline-flex shrink-0 items-center rounded-lg px-3 leading-4 transition-colors disabled:opacity-60',
            t.button,
            ctaSize === 'md' && 'h-8 text-[13px]',
          )}
        >
          {cta.label}
        </button>
      )}
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className={cn('flex size-6 shrink-0 items-center justify-center rounded-md hover:bg-black/5', t.text)}
        >
          <HugeiconsIcon icon={Cancel01Icon} size={14} strokeWidth={2.2} />
        </button>
      )}
    </div>
  )
}

/** 12px glyphs drawn to match the Paper chips (clock, card, lock, pause). */
export function ChipGlyph({ kind, className }: { kind: 'clock' | 'card' | 'lock' | 'pause'; className?: string }) {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" aria-hidden className={cn('shrink-0', className)}>
      {kind === 'clock' && (
        <>
          <circle cx="12" cy="12" r="8.5" fill="none" stroke="currentColor" strokeWidth="2.2" />
          <path d="M12 7.5V12l3 2" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
        </>
      )}
      {kind === 'card' && (
        <>
          <rect x="3" y="5.5" width="18" height="13" rx="2.5" fill="none" stroke="currentColor" strokeWidth="2.2" />
          <path d="M3 10h18" stroke="currentColor" strokeWidth="2.2" />
        </>
      )}
      {kind === 'lock' && (
        <>
          <rect x="5" y="11" width="14" height="10" rx="2" fill="none" stroke="currentColor" strokeWidth="2.2" />
          <path d="M8 11V8a4 4 0 0 1 8 0v3" fill="none" stroke="currentColor" strokeWidth="2.2" />
        </>
      )}
      {kind === 'pause' && (
        <>
          <rect x="6" y="5" width="4" height="14" rx="1" fill="currentColor" />
          <rect x="14" y="5" width="4" height="14" rx="1" fill="currentColor" />
        </>
      )}
    </svg>
  )
}

/** 28×4 usage meter for the quota chip ("75% used"). */
export function ChipMeter({ ratio }: { ratio: number }) {
  return (
    <span aria-hidden className="flex h-1 w-7 shrink-0 rounded-[2px] bg-urgency-amber-accent/30">
      <span
        className="h-1 rounded-[2px] bg-urgency-amber-fg"
        style={{ width: `${Math.round(Math.min(1, Math.max(0, ratio)) * 28)}px` }}
      />
    </span>
  )
}
